import { useQuery } from '@tanstack/react-query';
import { api, qs, unwrap } from '../api/client';
import type { DailyPoint, Dashboard, Insights } from '../api/types';
import { Card, ErrorAlert, fmtPct, PageHeader, Stat } from '../components/ui';

/** Pure SVG line chart: no chart library, no state. */
export function LineChart({
  series,
  labels,
}: {
  series: ReadonlyArray<{ name: string; color: string; values: readonly number[] }>;
  labels: readonly string[];
}) {
  const w = 720;
  const h = 200;
  const pad = 28;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const n = Math.max(1, labels.length - 1);
  const x = (i: number) => pad + (i * (w - pad * 2)) / n;
  const y = (v: number) => h - pad - (v / max) * (h - pad * 2);
  return (
    <div>
      <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Line chart">
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={pad} x2={w - pad} y1={y(max * t)} y2={y(max * t)} stroke="#e3e5ee" />
            <text x={4} y={y(max * t) + 4} fontSize="10" fill="#6b7083">
              {Math.round(max * t)}
            </text>
          </g>
        ))}
        {series.map((s) => (
          <polyline
            key={s.name}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
          />
        ))}
        {labels.map((l, i) =>
          i % Math.ceil(labels.length / 8) === 0 ? (
            <text key={l} x={x(i)} y={h - 8} fontSize="10" textAnchor="middle" fill="#6b7083">
              {l.slice(5)}
            </text>
          ) : null,
        )}
      </svg>
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.name}>
            <i style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const dash = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => unwrap(api.get<Dashboard>('/portal/analytics/dashboard')),
  });
  const ins = useQuery({
    queryKey: ['insights', 30],
    queryFn: () => unwrap(api.get<Insights>(`/portal/analytics/insights${qs({ days: 30 })}`)),
  });
  const d = dash.data;
  // UI-54 (revised): nothing has happened yet (a connected account alone is not activity)
  // UI-54 (revised 2): nothing has run and nothing was sent; a published automation is not activity yet
  const fresh = d !== undefined && d.automationRuns === 0 && d.messagesSent === 0;
  // UI-19: link analytics are Legend and above; say so instead of drawing empty charts
  const gated = (ins.error as { code?: string } | null)?.code === 'plan_feature_unavailable';
  // ANA-16: message volume is for every plan; the clicks chart stays with the gated insights
  const volume = useQuery({
    queryKey: ['daily-volume', 30],
    queryFn: () =>
      unwrap(api.get<{ items: DailyPoint[] }>(`/portal/analytics/daily${qs({ days: 30 })}`)),
  });
  const messageDays = volume.data?.items ?? [];
  const daily = ins.data?.clicks.daily ?? [];
  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of connected accounts, automations and messaging performance"
      />
      <ErrorAlert error={dash.error} />
      {fresh ? (
        // UI-54 (revised): a workspace with no activity is told what to do first; the tiles and
        // charts wait until an automation has run or a message has been sent
        <div className="alert alert-info" data-testid="dashboard-first-steps">
          Nothing to show yet. Connect an Instagram account and publish an automation to see
          activity here.
        </div>
      ) : null}
      {d && !fresh ? (
        <div className="grid grid-4">
          <Stat label="Connected accounts" value={d.connectedAccounts} />
          <Stat label="Active automations" value={d.activeAutomations} />
          <Stat label="Messages sent" value={d.messagesSent} />
          {/* ANA-15 (revised): the PRD's words, with what they mean on Instagram */}
          <Stat
            label="Messages delivered"
            value={d.messagesDelivered}
            hint="accepted by Instagram's API"
          />
          {/* ANA-24: Instagram's read receipts (messaging_seen) */}
          {d.messagesRead !== undefined ? (
            <Stat
              label="Read"
              value={d.messagesRead}
              hint={`${fmtPct(d.readRate ?? 0)} of delivered`}
            />
          ) : null}
          <Stat label="Delivery rate" value={fmtPct(d.deliveryRate)} />
          <Stat label="Replies" value={d.replies} />
          {d.linkClicks !== null ? <Stat label="Link clicks" value={d.linkClicks} /> : null}
          {d.conversions !== null ? <Stat label="Conversions" value={d.conversions} /> : null}
          <Stat label="Automation runs" value={d.automationRuns} />
          {/* DLV-19: DMs held while a human owns the thread are not failures */}
          <Stat
            label="Failed messages"
            value={d.messagesFailed}
            {...(d.messagesHeld ? { hint: `${d.messagesHeld} held for a human, not counted` } : {})}
          />
          {/* ANA-23 */}
          <Stat label="Contacts generated" value={d.contacts} />
        </div>
      ) : null}
      {gated && !fresh ? (
        <div className="alert alert-info" data-testid="insights-gated">
          Link analytics and the advanced dashboard are part of the Legend plan and above. Upgrade
          in Settings to see daily clicks and conversions here; message volume is below.
        </div>
      ) : null}

      {fresh ? null : (
        <div className="grid grid-2" style={{ marginTop: 16 }}>
          <Card title="Message volume (30 days)">
            <LineChart
              labels={messageDays.map((p) => p.day)}
              series={[
                { name: 'Sent', color: '#5b5bd6', values: messageDays.map((p) => p.sent) },
                { name: 'Accepted', color: '#15803d', values: messageDays.map((p) => p.delivered) },
                { name: 'Failed', color: '#b91c1c', values: messageDays.map((p) => p.failed) },
              ]}
            />
          </Card>
          {gated ? null : (
            <Card title="Clicks vs conversions (30 days)">
              <LineChart
                labels={daily.map((p) => p.day)}
                series={[
                  { name: 'Clicks', color: '#b45309', values: daily.map((p) => p.clicks) },
                  {
                    name: 'Conversions',
                    color: '#15803d',
                    values: daily.map((p) => p.conversions),
                  },
                ]}
              />
            </Card>
          )}
        </div>
      )}
    </>
  );
}
